import type { ProjectMember } from '@/features/auth/types';
import type { Personnel } from '@/features/personnel/types';
import { sanitizeDocumentForFirestore } from '@/utils/firestore-sanitizer';
import type { ProjectScope } from './project-scope';
import { createAndStoreProjectWriteIntent } from './project-write-operation-factory';

export type PersonnelProfilePatch = Pick<Personnel, 'id'> & Record<string, unknown>;

const OWN_PUBLIC_PROFILE_WRITE_FIELDS = new Set([
    'id',
    'preferredName',
    'pronouns',
    'phone',
]);

const OWN_SENSITIVE_PROFILE_WRITE_FIELDS = new Set([
    'id',
    'emergencyName',
    'emergency',
    'emergencyPhone',
    'allergies',
    'accessNeeds',
    'bio',
    'headshotUrl',
    'headshotFileId',
    'headshotStoragePath',
    'measurementNotes',
    'notificationPreference',
    'emailOptOut',
]);

const SELF_PROFILE_LINK_METADATA_FIELDS = new Set([
    'linkedUserId',
]);

function patchUsesOnlyFields(patch: PersonnelProfilePatch, fields: Set<string>): boolean {
    return Object.keys(patch).every(key => fields.has(key));
}

function pickProfilePatchFields(patch: PersonnelProfilePatch, fields: Set<string>): PersonnelProfilePatch | null {
    const picked: PersonnelProfilePatch = { id: patch.id };
    for (const [key, value] of Object.entries(patch)) {
        if (key === 'id') continue;
        if (fields.has(key)) {
            Object.assign(picked, { [key]: value });
        }
    }
    return Object.keys(picked).length > 1 ? picked : null;
}

type SensitivePersonnelProfileWrite = {
    conflictPrefix: 'personnelSensitive' | 'personnelSensitiveSelf';
    capability: 'EDIT_PERSONNEL_OPERATIONAL' | 'UPDATE_OWN_PROFILE';
    patch: PersonnelProfilePatch;
};

function pickOperationalProfilePatchFields(patch: PersonnelProfilePatch): PersonnelProfilePatch | null {
    const picked: PersonnelProfilePatch = { id: patch.id };
    for (const [key, value] of Object.entries(patch)) {
        if (
            key === 'id'
            || OWN_SENSITIVE_PROFILE_WRITE_FIELDS.has(key)
            || SELF_PROFILE_LINK_METADATA_FIELDS.has(key)
        ) {
            continue;
        }
        Object.assign(picked, { [key]: value });
    }
    return Object.keys(picked).length > 1 ? picked : null;
}

function resolveSensitivePersonnelProfileWrites(
    patch: PersonnelProfilePatch,
    ownsProfile: boolean
): SensitivePersonnelProfileWrite[] {
    if (!ownsProfile) {
        return [{
            conflictPrefix: 'personnelSensitive',
            capability: 'EDIT_PERSONNEL_OPERATIONAL',
            patch,
        }];
    }

    const writes: SensitivePersonnelProfileWrite[] = [];
    const ownPatch = pickProfilePatchFields(patch, OWN_SENSITIVE_PROFILE_WRITE_FIELDS);
    if (ownPatch) {
        writes.push({
            conflictPrefix: 'personnelSensitiveSelf',
            capability: 'UPDATE_OWN_PROFILE',
            patch: ownPatch,
        });
    }

    const operationalPatch = pickOperationalProfilePatchFields(patch);
    if (operationalPatch) {
        writes.push({
            conflictPrefix: 'personnelSensitive',
            capability: 'EDIT_PERSONNEL_OPERATIONAL',
            patch: operationalPatch,
        });
    }

    return writes;
}

export function mergePersonnelServerLinks(localPersonnel: Personnel[], serverPersonnel: Personnel[]): Personnel[] {
    const serverById = new Map(serverPersonnel.map(member => [member.id, member]));

    return localPersonnel.map(member => {
        const serverMember = serverById.get(member.id);
        if (!serverMember?.linkedUserId || member.linkedUserId === serverMember.linkedUserId) {
            return member;
        }

        return {
            ...member,
            linkedUserId: serverMember.linkedUserId,
        };
    });
}

export function applyProjectMemberPersonnelLinks(personnel: Personnel[], _members: ProjectMember[]): Personnel[] {
    return personnel;
}

export async function enqueuePublicPersonnelProfileWrite(input: {
    scope: ProjectScope;
    actorId: string;
    actorMember?: ProjectMember;
    person: Personnel;
    patch: PersonnelProfilePatch;
}): Promise<void> {
    const isOwnSelfPatch = input.actorMember?.personnelId === input.person.id
        && patchUsesOnlyFields(input.patch, OWN_PUBLIC_PROFILE_WRITE_FIELDS);
    const conflictPrefix = isOwnSelfPatch ? 'personnelPublicSelf' : 'personnelPublic';
    await createAndStoreProjectWriteIntent({
        scope: input.scope,
        actorId: input.actorId,
        feature: 'people_casting',
        capability: isOwnSelfPatch ? 'UPDATE_OWN_PROFILE' : 'EDIT_CAST',
        kind: 'patchEntity',
        conflictUnit: `${conflictPrefix}/${input.person.id}`,
        payload: sanitizeDocumentForFirestore(input.patch),
    });
}

export async function enqueueSensitivePersonnelProfileWrite(input: {
    scope: ProjectScope;
    actorId: string;
    actorMember?: ProjectMember;
    person: Personnel;
    patch: PersonnelProfilePatch;
}): Promise<void> {
    const writes = resolveSensitivePersonnelProfileWrites(
        input.patch,
        input.actorMember?.personnelId === input.person.id,
    );
    await Promise.all(
        writes.map(write => createAndStoreProjectWriteIntent({
            scope: input.scope,
            actorId: input.actorId,
            feature: 'people_casting',
            capability: write.capability,
            kind: 'patchEntity',
            conflictUnit: `${write.conflictPrefix}/${input.person.id}`,
            payload: sanitizeDocumentForFirestore(write.patch),
        }))
    );
}
